import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { StringParam, useQueryParams } from 'use-query-params';

import { FolderType } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorAccessDenied from '@/components/ErrorAccessDenied/ErrorAccessDenied';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { PermissionKeys, useAnyPermissions } from '@/hooks/permissions/usePermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import typography from '@/theme/typography';

import FolderStatusSelect from './FolderStatusSelect';
import SubFolderStatusSelect from './SubFolderStatusSelect';

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

interface FolderBasicInfoTabProps {
  type?: FolderType;
}

interface BasicInfoContentProps {
  statusComponent: React.ReactNode;
  nameLabel?: string;
  readonly?: boolean;
}

const BasicInfoContent: FC<BasicInfoContentProps> = ({ statusComponent, nameLabel, readonly = false }) => {
  const { t } = useTranslation('settings');
  const { spacing } = useTheme();
  const { isMidotUser } = useIsMidotUser();

  return (
    <ReadonlyProvider readonly={readonly}>
      <Stack
        width="100%"
        sx={{
          '& .MuiFormLabel-root': { textAlign: 'start' },
          '& .MuiFormControl-root': { pb: 0 },
          '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
        }}
      >
        <Stack direction="row" justifyContent="space-between" width="100%" p={3}>
          <Typography sx={{ ...typography.body1, fontWeight: typography.fontWeightMedium }}>
            {t('orgSettings.tabs.basicInfo')}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ ...typography.body3, fontWeight: typography.fontWeightMedium, color: 'grey.700' }}>
              {t('orgSettings.fields.status')}
            </Typography>
            <Box>{statusComponent}</Box>
          </Stack>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            mx: 3,
            gridColumnGap: spacing(2),
            gridRowGap: spacing(3),
            '& .MuiInputBase-root': { minHeight: spacing(5) },
            '& .MuiFormControl-root': { px: 0, pt: 0 },
            '& .DropdownSelect-control': { minHeight: spacing(5) }
          }}
        >
          <Field
            component={TextInput}
            name="name"
            label={nameLabel}
            innerProps={{
              sx: {
                '& .MuiInputAdornment-root': { display: 'none' }
              }
            }}
            withFormControl
          />
        </Box>

        {isMidotUser && (
          <Box
            sx={{
              display: 'grid',
              width: '67%',
              gridTemplateColumns: '1fr 1fr',
              mx: 3,
              mt: 3,
              gridColumnGap: spacing(2),
              gridRowGap: spacing(3),
              '& .MuiFormControl-root': { px: 0, pt: 0 },
              '& .MuiInputBase-root': { minHeight: spacing(5) }
            }}
          >
            <Field
              component={TextInput}
              name="newId"
              label={t('orgSettings.fields.accountNumbersNewId')}
              innerProps={{
                sx: {
                  '& .MuiInputAdornment-root': { display: 'none' },
                  '& .MuiInputBase-root': {
                    bgcolor: 'grey.200'
                  },
                  '& .MuiInputBase-root:hover fieldset': {
                    borderColor: 'grey.400'
                  },
                  color: 'grey.500'
                },
                disabled: true
              }}
              withFormControl
            />
            <Field
              component={TextInput}
              name="legacyId"
              label={t('orgSettings.fields.accountNumbersOldId')}
              innerProps={{
                sx: {
                  '& .MuiInputAdornment-root': { display: 'none' }
                },
                numbersOnly: true
              }}
              withFormControl
            />
          </Box>
        )}
      </Stack>
    </ReadonlyProvider>
  );
};

const FolderBasicInfoTab: FC<FolderBasicInfoTabProps> = ({ type }) => {
  const { t } = useTranslation('settings');
  const isFolder = type === FolderType.FOLDER;
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const permissionVariables = useMemo(
    () => ({
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType: type
    }),
    [locationSlug, organizationSlug, type]
  );

  const { value: canUpdateBasicInfo, isLoading } = useAnyPermissions({
    permissions: [PermissionKeys.FoldercanUpdateBasicInfo, PermissionKeys.SubfoldercanUpdateBasicInfo],
    variables: permissionVariables
  });

  const nameLabel = useMemo(
    () => t(isFolder ? 'orgSettings.fields.folderName' : 'orgSettings.fields.subFolderName'),
    [t, isFolder]
  );

  const statusComponent = useMemo(() => (isFolder ? <FolderStatusSelect /> : <SubFolderStatusSelect />), [isFolder]);

  if (isLoading) {
    return <BackdropLoader />;
  }

  return (
    <PermissionGuard
      all={[isFolder ? PermissionKeys.FoldercanViewBasicInfo : PermissionKeys.SubfoldercanViewBasicInfo]}
      variables={permissionVariables}
      accessDeniedFallback={<ErrorAccessDenied />}
      errorFallback={<ErrorGeneric />}
      loadingFallback={<BackdropLoader />}
    >
      <BasicInfoContent statusComponent={statusComponent} nameLabel={nameLabel} readonly={!canUpdateBasicInfo} />
    </PermissionGuard>
  );
};

export default FolderBasicInfoTab;
